import React from 'react'
import './edit-campaign.scss'
import { PageHeader } from '@pushly/aqe/lib/components'
import { BetterComponent } from '../../../components/better-component/better-component'
import { AppState } from '../../../stores/app'
import { AppService, CampaignV2Service, DomainIntegrationService, DomainService } from '../../../services'
import { Container } from 'typescript-ioc/es5'
import { CampaignBuilderMode } from '../../../components/campaign-builder/enums'
import { ifDomainChanged } from '../../../_utils/domain'
import { CampaignWizard } from '../../../components/campaign-wizard/campaign-wizard'
import { DomainDto } from '../../../dtos/domain'
import { StatusType } from '../../../enums/status-type'
import { UserType } from '../../../enums/user-type'
import { IServiceApiResponse } from '../../../interfaces/service-api-response'
import { onResponseError403 } from '../../../_utils/on-response-error-403'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { JourneyStatusRef } from '../types'

interface IEditCampaign {}

interface IState {
    loading: boolean
    domain: DomainDto
    loadedCampaign?: any
    campaign?: any
    integrations?: any[]
    campaignHasChanged: boolean
}

export class EditCampaign extends BetterComponent<IEditCampaign, IState> {
    private readonly appState: AppState
    private readonly appService: AppService
    private readonly domainService: DomainService
    private readonly campaignService: CampaignV2Service
    private readonly integrationsSvc: DomainIntegrationService

    private disposeObservers: any[] = []

    private wizardRef = React.createRef<JourneyStatusRef>()

    public constructor(props: IEditCampaign) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.domainService = Container.get(DomainService)
        this.campaignService = Container.get(CampaignV2Service)
        this.integrationsSvc = Container.get(DomainIntegrationService)

        this.state = {
            loading: false,
            domain: this.appState.currentDomain!,
            campaignHasChanged: false,
        }
    }

    public componentDidMount(): void {
        this.loadResources()

        this.disposeObservers.push(ifDomainChanged(this.appState, () => this.goBackToCampaignsList()))
    }

    public componentWillUnmount() {
        super.componentWillUnmount()

        this.disposeObservers.forEach((disposeFn) => disposeFn())
    }

    public render(): React.ReactNode {
        const { loading, campaign } = this.state
        const isDraftCampaign = campaign?.status === StatusType.DRAFT.name
        const headerPrefix = isDraftCampaign ? 'Draft' : 'Edit'
        const headerName = !campaign ? 'Campaign' : campaign.name

        return (
            <div className="edit-campaign">
                <PageHeader
                    title={`${loading ? 'Loading...' : `${headerPrefix}: ${headerName}`}`}
                    append={<span>ID: {this.campaignId ?? '...'}</span>}
                    onTitleSet={this.appService.customizeTabTitle}
                />

                <CampaignWizard
                    wizardRef={this.wizardRef}
                    loading={this.state.loading}
                    mode={CampaignBuilderMode.EDIT}
                    domain={this.state.domain}
                    campaign={this.state.campaign}
                    onChange={this.handleUpdate}
                    onSubmit={this.handleSubmit}
                    integrations={this.state.integrations}
                    tabsProps={{
                        destroyInactiveTabPane: true,
                    }}
                    canTabNavigate={() => {
                        if (this.wizardRef.current?.builderHasChanged) {
                            simpleNotification('error', 'You have unsaved changes. Please save before continuing.')
                            return false
                        }

                        return true
                    }}
                />
            </div>
        )
    }

    public get campaignId(): number {
        return this.injectedProps.match.params.campaignId
    }

    protected get templateId(): number {
        return this.queryString.template as any
    }

    protected goBackToCampaignsList() {
        this.appService.routeWithinDomain('/campaigns')
    }

    protected handleUpdate = async (campaign: any) => {
        return this.setState({ campaign })
    }

    protected handleSubmit = async (): Promise<any> => {
        // reload campaign and associated resources
        return this.loadResources()
    }

    protected loadResources = async (): Promise<void> => {
        let domain = this.state.domain
        const user = this.appState.currentUser

        await this.setState({
            campaign: undefined,
            loading: true,
        })

        const update: any = { loading: false }

        const loadRequests: any[] = []

        // fetch domain integrations
        const domainReqId = loadRequests.push(this.domainService.fetchById(domain.id)) - 1
        const integrationsReqId = loadRequests.push(this.integrationsSvc.fetchAll(domain.id)) - 1
        let campaignReqId: number | undefined

        if (this.campaignId) {
            campaignReqId =
                loadRequests.push(
                    this.campaignService.fetchById(this.campaignId, {
                        errorHandler: onResponseError403(() => {
                            this.goBackToCampaignsList()
                        }),
                    }),
                ) - 1
        }

        const responses = await Promise.all(loadRequests)

        const domainRes: IServiceApiResponse<DomainDto> = responses[domainReqId]
        const integrationsRes: IServiceApiResponse<any[]> = responses[integrationsReqId]

        let campaignRes: IServiceApiResponse<any> | undefined
        if (campaignReqId) {
            campaignRes = responses[campaignReqId]
        }

        if (domainRes.ok) {
            update.domain = domainRes.data
        }

        if (integrationsRes.ok) {
            update.integrations = integrationsRes.data as any[]
        }

        if (campaignRes?.ok) {
            const campaign = campaignRes.data
            campaign.campaign_type = campaign.type
            campaign.steps = campaign.revision?.steps
            update.loadedCampaign = campaign
            update.campaign = campaign
        }

        this.setState(update)
    }
}
